import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  chakra,
  Divider,
  Flex,
  HStack,
  Icon,
  keyframes,
  Link,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { LiaFileInvoiceDollarSolid } from 'react-icons/lia';
import Header from '../../../../../components/Header/Header';
import CardHeaderMobile from '../../../../../components/Mobile/header';
import LabelWithValueMobilePage from '../../../../../components/Mobile/labelWithValue';
import { useGetRegistration } from '../../../../../services/hooks/Registration/useGetRegistration';
import HeaderSectionMobilePage from '../../../../../components/Mobile/headerSection';
import TitleSectionMobilePage from '../../../../../components/Mobile/titleSection';
import { badgeColor } from '../../../../../utils/badgeFunction';
import RegistrationAditionInfoMobilePage from '../../Mobile/AditionalInfo';
import { IoDocumentTextOutline, IoLinkOutline } from 'react-icons/io5';
import ProofRegistrationButton from '../../../../../components/PDFs/DownloadPDFButton/proofRegistrationButton';

const floatIn = keyframes`
      from { opacity: 0; transform: translateY(18px) scale(0.995); }  
  `;

const AnimatedBox = chakra(Box, {
  baseStyle: {
    animation: `${floatIn} 420ms cubic-bezier(.2,.9,.3,1) both`,
  },
});

const PaymentReissueMobilePage: React.FC = () => {
  const registrationId = useLocation().pathname.split('/')[3];

  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');

  const getRegistration = useGetRegistration(registrationId || '');

  const isCultureHouse = getRegistration.data?.selective_process_vacancy.cultural_house;

  const modality = getRegistration?.data?.type_of_competition.reduce(
    (acc, next, index) => `${(acc += index !== 0 ? ' | ' : '')}${next.name}`,
    '',
  );

  const candidate = getRegistration.data?.candidate;

  return (
    <Stack height="100%" w="100%" top={0} bg="white" gap={0}>
      <Header showGoBack />
      <VStack mt={0} mx={3} flex={1} overflowY="auto" py={2}>
        {(getRegistration.isLoading && (
          <VStack flex={1} w="100%" justify="center">
            <Spinner emptyColor="gray.100" color="blue.500" size="xl" />
            <Text fontSize="sm">Buscando dados do boleto...</Text>
          </VStack>
        )) ||
          (getRegistration.isError && <Text>Erro ao trazer os dados.</Text>) || (
            <AnimatedBox
              bgColor="white"
              borderRadius="lg"
              boxShadow="lg"
              p={{ base: 5, md: 10 }}
              borderWidth={'1px'}
              borderColor={borderColor}
              w="full"
              display="flex"
            >
              <VStack boxSizing="border-box" flex={1} spacing={4} align="stretch">
                <CardHeaderMobile
                  title={`Inscrição Nº ${getRegistration?.data?.registration_code}`}
                  subTitle="Caso ainda não tenha realizado o pagamento e precise solicitar, baixar ou reimprimir a segunda via, clique no botão Gerar Dados para Pagamento, selecione o meio de pagamento e emita a segunda via."
                  colorScheme="green"
                  badgeIcon={<LiaFileInvoiceDollarSolid size={13} />}
                  gap={1}
                  px={3}
                  badgeText="Detalhes"
                />
                <VStack spacing={2} align="stretch" h="100%">
                  <HeaderSectionMobilePage>
                    {getRegistration.data?.selective_process_vacancy?.selective_process?.name}
                  </HeaderSectionMobilePage>
                  <Stack></Stack>
                  <LabelWithValueMobilePage label="Inscrição" value={getRegistration.data?.registration_code} />
                  <LabelWithValueMobilePage label="Nome completo" value={candidate?.user.full_name} />
                  {candidate?.social_name && (
                    <LabelWithValueMobilePage label="Nome social" value={candidate?.social_name || ''} />
                  )}
                  <LabelWithValueMobilePage
                    label={candidate?.is_foreign ? 'Passaporte' : 'CPF'}
                    value={candidate?.is_foreign ? candidate?.passport_number : candidate?.cpf}
                  />
                  {getRegistration.data?.selective_process_vacancy?.name && (
                    <LabelWithValueMobilePage
                      label={
                        getRegistration.data?.selective_process_vacancy?.cultural_house
                          ? getRegistration.data?.second_vacancy
                            ? 'Turma - Primeira opção: '
                            : 'Turma '
                          : 'Vaga/Cargo: '
                      }
                      value={getRegistration.data?.selective_process_vacancy.name}
                    />
                  )}
                  {getRegistration.data?.second_vacancy?.name && (
                    <>
                      <LabelWithValueMobilePage
                        label="Turma - Segunda opção"
                        value={getRegistration.data?.second_vacancy?.name}
                      />
                    </>
                  )}
                  <LabelWithValueMobilePage label="Situação" value={getRegistration.data?.registration_status.name} />
                  {getRegistration?.data?.observations && (
                    <LabelWithValueMobilePage label="Observação" value={getRegistration?.data?.observations} />
                  )}
                  {getRegistration.data?.invoice?.status_invoice && (
                    <LabelWithValueMobilePage
                      label="Situação do boleto"
                      value={getRegistration.data?.invoice?.status_invoice}
                    />
                  )}
                  {!getRegistration?.data?.selective_process_vacancy?.cultural_house &&
                    getRegistration?.data?.type_of_competition &&
                    getRegistration?.data?.type_of_competition?.length > 0 && (
                      <LabelWithValueMobilePage label="Modalidade" value={modality} />
                    )}
                </VStack>
                {getRegistration.data?.reserved_vacancy && getRegistration.data?.reserved_vacancy?.length > 0 && (
                  <Stack mt={2} gap={2}>
                    <TitleSectionMobilePage>Vagas reservadas</TitleSectionMobilePage>
                    {getRegistration.data?.reserved_vacancy.map((reservedVacancy, index) => {
                      const colorBadge = badgeColor(reservedVacancy.status);

                      return (
                        <Box
                          key={index}
                          bg="white"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.200"
                          p={3}
                          pb={2}
                          transition="0.2s"
                        >
                          <VStack align="stretch" spacing={2}>
                            <Flex justify="space-between" align="center" wrap="wrap" gap={5}>
                              <Text fontWeight="semibold" color="gray.800">
                                {reservedVacancy.type}
                              </Text>
                              <Badge
                                bgColor={colorBadge?.bgColor}
                                color={colorBadge?.color}
                                px={2}
                                py={0}
                                borderRadius="sm"
                                textTransform="capitalize"
                                fontSize="10px"
                              >
                                {reservedVacancy.status}
                              </Badge>
                            </Flex>
                            <Divider py={0} />
                            <HStack justify="center">
                              <RegistrationAditionInfoMobilePage titleModalizer="Detalhes de vagas reservadas">
                                <Stack spacing={5} px={0} pb={8} maxH="80dvh" overflowY="auto">
                                  <Card
                                    bg={cardBg}
                                    border="1px solid"
                                    borderColor={borderColor}
                                    rounded="xl"
                                    shadow="md"
                                  >
                                    <CardBody p={4}>
                                      <Flex align="center" justify="space-between" mb={3}>
                                        <Text fontSize="md" fontWeight="bold" color="teal.600">
                                          Informações da solicitação
                                        </Text>
                                        <Icon as={IoDocumentTextOutline} color="teal.500" boxSize={4} />
                                      </Flex>

                                      <VStack spacing={3} align="stretch">
                                        <LabelWithValueMobilePage
                                          label="Processo Seletivo"
                                          value={
                                            getRegistration.data?.selective_process_vacancy?.selective_process?.name ||
                                            '--'
                                          }
                                        />
                                        <LabelWithValueMobilePage
                                          label={
                                            isCultureHouse
                                              ? `Turma ${(getRegistration.data?.second_vacancy && ' - primeira opção') || ''}`
                                              : 'Vaga / Cargo'
                                          }
                                          value={getRegistration.data?.selective_process_vacancy.name}
                                        />
                                        {getRegistration.data?.second_vacancy && (
                                          <LabelWithValueMobilePage
                                            label="Turma - segunda opção"
                                            value={getRegistration.data?.second_vacancy.name}
                                          />
                                        )}
                                        <LabelWithValueMobilePage label="Tipo" value={reservedVacancy.type} />
                                        {reservedVacancy.cid && (
                                          <LabelWithValueMobilePage label="CID" value={reservedVacancy.cid} />
                                        )}
                                        {reservedVacancy.deficiency && (
                                          <LabelWithValueMobilePage
                                            label="Detalhe"
                                            value={reservedVacancy.deficiency}
                                          />
                                        )}
                                      </VStack>

                                      <Divider my={3} />

                                      <VStack align="stretch" spacing={3}>
                                        <Flex align="center" gap={2}>
                                          <Text fontWeight="bold" fontSize="sm">
                                            Situação da solicitação:
                                          </Text>
                                          <Badge
                                            bgColor={colorBadge?.bgColor}
                                            color={colorBadge?.color}
                                            p={1}
                                            px={2}
                                            rounded="sm"
                                            fontSize="10px"
                                          >
                                            {reservedVacancy.status}
                                          </Badge>
                                        </Flex>

                                        {reservedVacancy.justification && (
                                          <LabelWithValueMobilePage
                                            label="Justificativa"
                                            value={reservedVacancy.justification}
                                          />
                                        )}
                                      </VStack>
                                    </CardBody>
                                  </Card>
                                  {reservedVacancy?.attachments?.length && (
                                    <Card
                                      bg={cardBg}
                                      border="1px solid"
                                      borderColor={borderColor}
                                      rounded="xl"
                                      shadow="md"
                                    >
                                      <CardBody p={4}>
                                        <Flex align="center" justify="space-between" mb={3}>
                                          <Text fontSize="md" fontWeight="bold" color="blue.600">
                                            Anexos
                                          </Text>
                                        </Flex>

                                        <VStack spacing={2} align="stretch">
                                          {reservedVacancy.attachments.map((file, idx) => (
                                            <Link
                                              key={idx}
                                              href={file.url}
                                              isExternal
                                              _hover={{ textDecoration: 'none' }}
                                            >
                                              <Button
                                                leftIcon={<IoLinkOutline />}
                                                variant="outline"
                                                colorScheme="blue"
                                                size="sm"
                                                w="full"
                                                justifyContent="flex-start"
                                                fontWeight="medium"
                                              >
                                                Anexo {idx + 1}
                                              </Button>
                                            </Link>
                                          ))}
                                        </VStack>
                                      </CardBody>
                                    </Card>
                                  )}
                                </Stack>
                              </RegistrationAditionInfoMobilePage>
                            </HStack>
                          </VStack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
                {getRegistration.data?.special_conditions && getRegistration.data?.special_conditions?.length > 0 && (
                  <Stack mt={2} gap={1}>
                    <TitleSectionMobilePage>Atendimento especial</TitleSectionMobilePage>
                    {getRegistration.data?.special_conditions.map((specialCondition, index) => {
                      const colorBadge = badgeColor(specialCondition.status);

                      return (
                        <Box
                          key={index}
                          bg="white"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.200"
                          p={3}
                          pb={2}
                          transition="0.2s"
                        >
                          <VStack align="stretch" spacing={2}>
                            <Flex justify="space-between" align="center" wrap="wrap" gap={5}>
                              <Text fontWeight="semibold" color="gray.800">
                                {specialCondition.name}
                              </Text>
                              <Badge
                                bgColor={colorBadge?.bgColor}
                                color={colorBadge?.color}
                                px={2}
                                py={0}
                                borderRadius="sm"
                                textTransform="capitalize"
                                fontSize="10px"
                              >
                                {specialCondition.status}
                              </Badge>
                            </Flex>
                            <Divider py={0} />
                            <HStack justify="center">
                              <RegistrationAditionInfoMobilePage titleModalizer="Detalhes do atendimento especial">
                                <Stack spacing={5} px={0} pb={8} maxH="80dvh" overflowY="auto">
                                  <Card
                                    bg={cardBg}
                                    border="1px solid"
                                    borderColor={borderColor}
                                    rounded="xl"
                                    shadow="md"
                                  >
                                    <CardBody p={4}>
                                      <Flex align="center" justify="space-between" mb={3}>
                                        <Text fontSize="md" fontWeight="bold" color="teal.600">
                                          Informações da solicitação
                                        </Text>
                                        <Icon as={IoDocumentTextOutline} color="teal.500" boxSize={4} />
                                      </Flex>

                                      <VStack spacing={3} align="stretch">
                                        <LabelWithValueMobilePage
                                          label="Processo Seletivo"
                                          value={
                                            getRegistration.data?.selective_process_vacancy?.selective_process?.name ||
                                            '--'
                                          }
                                        />
                                        <LabelWithValueMobilePage
                                          label={
                                            isCultureHouse
                                              ? `Turma ${(getRegistration.data?.second_vacancy && ' - primeira opção') || ''}`
                                              : 'Vaga / Cargo'
                                          }
                                          value={getRegistration.data?.selective_process_vacancy.name}
                                        />
                                        {getRegistration.data?.second_vacancy && (
                                          <LabelWithValueMobilePage
                                            label="Turma - segunda opção"
                                            value={getRegistration.data?.second_vacancy.name}
                                          />
                                        )}
                                        <LabelWithValueMobilePage label="Tipo" value={specialCondition.name} />
                                        <LabelWithValueMobilePage
                                          label="Detalhe"
                                          value={specialCondition.observations}
                                          acceptTag
                                        />
                                      </VStack>

                                      <Divider my={3} />

                                      <VStack align="stretch" spacing={3}>
                                        <Flex align="center" gap={2}>
                                          <Text fontWeight="bold" fontSize="sm">
                                            Situação da solicitação:
                                          </Text>
                                          <Badge
                                            bgColor={colorBadge?.bgColor}
                                            color={colorBadge?.color}
                                            p={1}
                                            px={2}
                                            rounded="sm"
                                            fontSize="10px"
                                          >
                                            {specialCondition.status}
                                          </Badge>
                                        </Flex>

                                        {specialCondition.justification && (
                                          <LabelWithValueMobilePage
                                            label="Justificativa"
                                            value={specialCondition.justification}
                                            acceptTag
                                          />
                                        )}
                                      </VStack>
                                    </CardBody>
                                  </Card>
                                  {specialCondition?.attachments?.length && (
                                    <Card
                                      bg={cardBg}
                                      border="1px solid"
                                      borderColor={borderColor}
                                      rounded="xl"
                                      shadow="md"
                                    >
                                      <CardBody p={4}>
                                        <Flex align="center" justify="space-between" mb={3}>
                                          <Text fontSize="md" fontWeight="bold" color="blue.600">
                                            Anexos
                                          </Text>
                                        </Flex>

                                        <VStack spacing={2} align="stretch">
                                          {specialCondition.attachments.map((file, idx) => (
                                            <Link
                                              key={idx}
                                              href={file.url}
                                              isExternal
                                              _hover={{ textDecoration: 'none' }}
                                            >
                                              <Button
                                                leftIcon={<IoLinkOutline />}
                                                variant="outline"
                                                colorScheme="blue"
                                                size="sm"
                                                w="full"
                                                justifyContent="flex-start"
                                                fontWeight="medium"
                                              >
                                                Anexo {idx + 1}
                                              </Button>
                                            </Link>
                                          ))}
                                        </VStack>
                                      </CardBody>
                                    </Card>
                                  )}
                                </Stack>
                              </RegistrationAditionInfoMobilePage>
                            </HStack>
                          </VStack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
                <Box mt={2}>
                  <ProofRegistrationButton data={getRegistration.data} />
                </Box>
              </VStack>
            </AnimatedBox>
          )}
      </VStack>
    </Stack>
  );
};

export default PaymentReissueMobilePage;
